import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/**
 * Trademarks & legal-notices page. Linked from the global footer.
 *
 * Purpose: a single, plain-English statement that no third-party brand
 * referenced anywhere on this site is owned by, affiliated with, or
 * endorsing Bidorah. Acknowledges nominative-fair-use of competitor and
 * service-provider names.
 */
export default function Notices() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="notices-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-14">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-3">
          Legal
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-900">
          Trademarks &amp; Notices
        </h1>
        <p className="mt-4 text-zinc-600 text-base leading-relaxed">
          Plain-English statement of every brand and service mentioned on
          this site, and how Bidorah relates to each.
        </p>

        <Section title="Bidorah marks">
          <p>
            <b>Bidorah</b>, <b>Bidoria</b>, the Bidorah wordmark, the Bidorah
            logo, and the slogan <i>"Bid live. Win real."</i> are trademarks
            of Bidorah, Inc. Bidoria<sup>™</sup> is original technology owned
            by Bidorah and is the subject of a pending US provisional patent
            application.
          </p>
        </Section>

        <Section title="Third-party trademarks">
          <p>
            All other product, brand, hashtag, and company names referenced
            anywhere on this site — including but not limited to those
            referenced for the purposes of comparative advertising, network
            integration, or platform format descriptors — are the property
            of their respective owners.
          </p>
          <p className="mt-3">
            Use of any third-party name on this site is for the purposes of
            identification, comparison, or compatibility only. No
            affiliation, sponsorship, or endorsement by, or partnership
            with, those third parties is claimed or implied.
          </p>
        </Section>

        <Section title="Comparative claims">
          <p>
            Any feature comparisons we publish reflect publicly known
            features as of the date shown on the comparison. Features
            change. If you spot a comparison that's out of date or
            inaccurate, email{" "}
            <a
              href="mailto:legal@bidorah.com"
              className="text-[#1D78AB] hover:underline font-semibold"
            >
              legal@bidorah.com
            </a>{" "}
            and we'll update it within 5 business days.
          </p>
        </Section>

        <Section title="Service integrations">
          <p>
            Bidorah integrates with third-party services including{" "}
            <b>Stripe</b> (payments), <b>PayPal</b> (payments),{" "}
            <b>LiveKit</b> (video infrastructure), <b>Resend</b>{" "}
            (transactional email), and <b>PSA / SGC / BGS / StockX</b>{" "}
            (third-party authentication graders we deep-link to).
            Mentioning these names is required to truthfully describe what
            powers parts of the platform; it is not an endorsement by
            those services of Bidorah, nor an endorsement by Bidorah of
            those services.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Trademark, IP, or attribution concerns:{" "}
            <a
              href="mailto:legal@bidorah.com"
              className="text-[#1D78AB] hover:underline font-semibold"
            >
              legal@bidorah.com
            </a>
            .
          </p>
        </Section>

        <p className="mt-12 text-xs text-zinc-500">
          Last updated: {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          .
        </p>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display font-black text-xl tracking-tight text-zinc-900 mb-3">
        {title}
      </h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}
